import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { HEADER_DESKTOP_SCROLL_DARK_CONFIG } from './header-desktop-scroll-dark-config';

gsap.registerPlugin(ScrollTrigger);

function persistHeaderDesktopDark(dark: boolean): void {
  const { storageKey, htmlClass } = HEADER_DESKTOP_SCROLL_DARK_CONFIG;
  try {
    sessionStorage.setItem(storageKey, dark ? '1' : '0');
  } catch {
    /* private mode / запрет storage */
  }
  document.documentElement.classList.toggle(htmlClass, dark);
}

function isHomeNamespace(): boolean {
  const container = document.querySelector<HTMLElement>('[data-barba="container"]');
  const namespace = container?.dataset.barbaNamespace;

  if (namespace) {
    return namespace === 'index';
  }

  return container?.querySelector<HTMLElement>('[data-barba-namespace]')?.dataset.barbaNamespace === 'index';
}

export function initHeaderDesktopScrollDark(): void {
  const desktopHeader = document.querySelector<HTMLElement>('.header.header-desktop');
  const mobileHeader = document.querySelector<HTMLElement>('.header.header-mobile');
  if (!desktopHeader && !mobileHeader) return;

  const { desktopMediaQuery, thresholdPx } = HEADER_DESKTOP_SCROLL_DARK_CONFIG;
  const mobileMediaQuery = '(max-width: 991.98px)';
  let forcedIsHome: boolean | null = null;

  const mm = gsap.matchMedia();

  mm.add(desktopMediaQuery, () => {
    if (!desktopHeader) return () => undefined;

    const applyState = (isHome: boolean): void => {
      const dark = !isHome || window.scrollY > thresholdPx;
      const scrolled = !isHome || window.scrollY > 0;
      desktopHeader.classList.toggle('header-home-dynamic', isHome);
      desktopHeader.classList.toggle('header-fixed-white', !isHome);
      desktopHeader.classList.toggle('dark', dark);
      desktopHeader.classList.toggle('header--scrolled', scrolled);
      persistHeaderDesktopDark(isHome && dark);
    };

    const sync = (): void => {
      applyState(forcedIsHome ?? isHomeNamespace());
    };
    const syncAfterBarba = (): void => {
      forcedIsHome = null;
      sync();
      requestAnimationFrame(sync);
    };
    const syncBeforeBarbaLeave = (event: Event): void => {
      const customEvent = event as CustomEvent<{ nextNamespace?: string }>;
      forcedIsHome = customEvent.detail?.nextNamespace === 'index';
      sync();
    };

    const trigger = ScrollTrigger.create({
      trigger: document.body,
      start: 0,
      end: 'max',
      onUpdate: sync,
    });

    sync();
    window.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    window.addEventListener('stc:barba-after-enter', syncAfterBarba);
    window.addEventListener('stc:barba-before-leave', syncBeforeBarbaLeave);

    return () => {
      window.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
      window.removeEventListener('stc:barba-after-enter', syncAfterBarba);
      window.removeEventListener('stc:barba-before-leave', syncBeforeBarbaLeave);
      trigger.kill();
      desktopHeader.classList.remove('dark', 'header--scrolled');
      persistHeaderDesktopDark(false);
    };
  });

  mm.add(mobileMediaQuery, () => {
    if (!mobileHeader) return () => undefined;

    const applyState = (isHome: boolean): void => {
      const scrolled = !isHome || window.scrollY > 0;
      mobileHeader.classList.toggle('header-home-dynamic', isHome);
      mobileHeader.classList.toggle('header--scrolled', scrolled);
    };

    const sync = (): void => {
      applyState(forcedIsHome ?? isHomeNamespace());
    };
    const syncAfterBarba = (): void => {
      forcedIsHome = null;
      sync();
      requestAnimationFrame(sync);
    };
    const syncBeforeBarbaLeave = (event: Event): void => {
      const customEvent = event as CustomEvent<{ nextNamespace?: string }>;
      forcedIsHome = customEvent.detail?.nextNamespace === 'index';
      sync();
    };

    const trigger = ScrollTrigger.create({
      trigger: document.body,
      start: 0,
      end: 'max',
      onUpdate: sync,
    });

    sync();
    window.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    window.addEventListener('stc:barba-after-enter', syncAfterBarba);
    window.addEventListener('stc:barba-before-leave', syncBeforeBarbaLeave);

    return () => {
      window.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
      window.removeEventListener('stc:barba-after-enter', syncAfterBarba);
      window.removeEventListener('stc:barba-before-leave', syncBeforeBarbaLeave);
      trigger.kill();
      mobileHeader.classList.remove('header-home-dynamic', 'header--scrolled');
    };
  });
}
